import { useState, type RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { Box, CircleGauge, Download, Ellipsis, Filter, Globe2, Info, Layers, Locate, Table } from 'lucide-react';

import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { MapViewMode } from '../utils/basemaps';
import type { MapMode } from '../utils/mapLayers';
import type { PoiFeatureProperties } from '../utils/poiFeatureProperties';
import { nextSemanticZoomStop, prevSemanticZoomStop } from '../utils/semanticZoom';
import { MapControlButton } from './MapControlButton';
import { MapSearchBox } from './MapSearchBox';
import { MapZoomControl } from './MapZoomControl';

interface MapControlsProps {
    mapRef: RefObject<MapRef | null>;
    mode: MapMode;
    isLayersPanelOpen: boolean;
    onToggleLayersPanel: () => void;
    isFiltersPanelOpen: boolean;
    onToggleFiltersPanel: () => void;
    isDownloadPanelOpen: boolean;
    onToggleDownloadPanel: () => void;
    isMapKeyPanelOpen: boolean;
    onToggleMapKeyPanel: () => void;
    viewMode: MapViewMode;
    onViewModeChange: (mode: MapViewMode) => void;
    onPoiSelect?: (poi: PoiFeatureProperties, coords: [number, number]) => void;
    onDmaSelect?: (ident: string) => void;
    /** Fired when the user clears the map search (releases search-originated highlights). */
    onSearchCleared?: () => void;
    /** True when the Network Overview table is open (split or maximised). */
    isTableViewOpen?: boolean;
    /** Toggle the Network Overview table open/closed. Find & Fix only. */
    onToggleTableView?: () => void;
    /**
     * Back to the network view — flies out, clears any clicked AOI/DMA and returns the Global
     * Stats bar to network totals. Owned by MapView so this button and the stats bar's Reset
     * control share one behaviour.
     */
    onLocateNetwork: () => void;
    /** True while the radial measuring tool owns the map (crosshair, feature clicks suppressed). */
    isRadialToolActive: boolean;
    /** Toggle the radial measuring tool on/off. */
    onToggleRadialTool: () => void;
}

export function MapControls({
    mapRef,
    mode,
    isLayersPanelOpen,
    onToggleLayersPanel,
    isFiltersPanelOpen,
    onToggleFiltersPanel,
    isDownloadPanelOpen,
    onToggleDownloadPanel,
    isMapKeyPanelOpen,
    onToggleMapKeyPanel,
    viewMode,
    onViewModeChange,
    onPoiSelect,
    onDmaSelect,
    onSearchCleared,
    isTableViewOpen,
    onToggleTableView,
    onLocateNetwork,
    isRadialToolActive,
    onToggleRadialTool,
}: MapControlsProps) {
    const isGlobe = viewMode === 'globe';
    const is3d = viewMode === '3d';
    const isSavi = mode === 'savi';
    const isExplorer = mode === 'network-explorer';
    // The single-client POI workflow controls (search, table, download) belong to Find & Fix
    // alone: SAVI has its own surfaces, and Network Explorer spans every client, where none of
    // them can operate safely.
    const showEntityControls = mode === 'find-and-fix';
    // Filters is the one entity control the explorer shares: its panel filters the estate's
    // markers, bubbles and statistics together, under the explorer's own filter state. The
    // tool controls keep their middle-column position in every mode, so Radial Tool, Filters
    // and Layers sit where Find & Fix users already know them.
    const showFilters = showEntityControls || isExplorer;

    // On mobile only Search, the More Controls toggle, Locate Network and the zoom buttons are
    // always visible; everything else collapses behind the toggle to reclaim vertical space.
    // Gated in JS (not md: classes) because the bundle emits some base utilities after their
    // md: variants, which would make the base style win at all widths.
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const [isMobileControlsExpanded, setIsMobileControlsExpanded] = useState(false);
    const showSecondaryControls = isDesktop || isMobileControlsExpanded;

    function handleGlobeToggle() {
        onViewModeChange(isGlobe ? '2d' : 'globe');
    }

    function handle3dToggle() {
        onViewModeChange(is3d ? '2d' : '3d');
    }

    // The +/- buttons snap through the discrete semantic zoom stops (each level's band start and
    // its mid-band intermediate; Network Explorer adds its outer world stops) rather than MapLibre's
    // native ±1, so they step in lockstep with the semantic zoom control.
    function handleZoomIn() {
        const map = mapRef.current?.getMap();
        if (!map) return;
        map.easeTo({ zoom: nextSemanticZoomStop(map.getZoom(), mode) });
    }

    function handleZoomOut() {
        const map = mapRef.current?.getMap();
        if (!map) return;
        map.easeTo({ zoom: prevSemanticZoomStop(map.getZoom(), mode) });
    }

    const toolControls = showSecondaryControls && (
        <>
            <MapControlButton
                icon={CircleGauge}
                label="Radial Tool"
                onClick={onToggleRadialTool}
                isActive={isRadialToolActive}
            />
            {/* Downloads are per client (POIs, or SAVI's high-risk valves): none across the estate. */}
            {!isExplorer && (
                <MapControlButton
                    icon={Download}
                    label={isSavi ? 'Download High Risk' : "Download POI's"}
                    onClick={onToggleDownloadPanel}
                    isActive={isDownloadPanelOpen}
                />
            )}
            {/* Filters: the single-client POI workflow and the explorer alike; SAVI has none. */}
            {showFilters && (
                <MapControlButton
                    icon={Filter}
                    label="Filters"
                    onClick={onToggleFiltersPanel}
                    isActive={isFiltersPanelOpen}
                />
            )}
            <MapControlButton icon={Layers} label="Layers" onClick={onToggleLayersPanel} isActive={isLayersPanelOpen} />
        </>
    );

    return (
        <div className="pointer-events-none absolute inset-y-4 right-4 z-10 flex flex-col justify-between">
            <div className="pointer-events-auto flex flex-col gap-2">
                {/* Search is a single-client POI workflow — Find & Fix only. */}
                {showEntityControls && (
                    <MapSearchBox
                        mapRef={mapRef}
                        onPoiSelect={onPoiSelect}
                        onDmaSelect={onDmaSelect}
                        onSearchCleared={onSearchCleared}
                    />
                )}
                {!isDesktop && (
                    <MapControlButton
                        icon={Ellipsis}
                        label="More Controls"
                        onClick={() => {
                            setIsMobileControlsExpanded((expanded) => !expanded);
                        }}
                        isActive={isMobileControlsExpanded}
                    />
                )}
                {/* SAVI™ Map Key — SAVI only. */}
                {showSecondaryControls && isSavi && (
                    <MapControlButton
                        icon={Info}
                        label="SAVI™ Map Key"
                        onClick={onToggleMapKeyPanel}
                        isActive={isMapKeyPanelOpen}
                    />
                )}
                {/* Table View — Find & Fix only (POI-centric workflow). */}
                {showSecondaryControls && showEntityControls && (
                    <MapControlButton
                        icon={Table}
                        label="Table View"
                        onClick={onToggleTableView ?? (() => {})}
                        isActive={isTableViewOpen}
                    />
                )}
            </div>

            {showSecondaryControls && <div className="pointer-events-auto flex flex-col gap-2">{toolControls}</div>}

            <div className="pointer-events-auto flex flex-col gap-2">
                {showSecondaryControls && (
                    <MapControlButton icon={Globe2} label="Globe View" onClick={handleGlobeToggle} isActive={isGlobe} />
                )}
                {showSecondaryControls && (
                    <MapControlButton icon={Box} label="3D View" onClick={handle3dToggle} isActive={is3d} />
                )}
                {/* In Network Explorer the "network" is the globe: the same control returns to the world view. */}
                <MapControlButton
                    icon={Locate}
                    label={isExplorer ? 'Reset View' : 'Locate Network'}
                    onClick={onLocateNetwork}
                />
                <MapZoomControl onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
            </div>
        </div>
    );
}
